import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, FileText } from "lucide-react";
import { IMAGES, HERO_STATS } from "@/data";
import { scrollTo } from "@/components/Nav";
import WaveDivider from "@/components/WaveDivider";
import WhaleShark from "@/components/WhaleShark";

export default function Hero() {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
    const bgY = useTransform(scrollYProgress, [0, 1], ["-6%", "10%"]);

    return (
        <section id="hero" ref={ref} className="relative scroll-mt-24" data-testid="hero-section">
            <div className="relative min-h-[92vh] flex flex-col justify-end overflow-hidden">
                <motion.div
                    className="absolute inset-0 scale-[1.15] bg-cover bg-center will-change-transform"
                    style={{ backgroundImage: `url(${IMAGES.hero})`, y: bgY }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#020D18] via-[#020D18]/70 to-[#020D18]/40" />
                <WhaleShark
                    className="right-[-60px] bottom-36 w-[540px] hidden lg:block"
                    opacity={0.3}
                    testid="hero-whale"
                />

                <div className="relative max-w-7xl mx-auto px-6 lg:px-8 w-full pt-40 pb-10">
                    <motion.div
                        initial={{ opacity: 0, y: 32 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.8, ease: "easeOut" }}
                        className="max-w-3xl"
                    >
                        <p className="text-sm font-semibold tracking-[0.3em] uppercase text-sky-200 mb-6" data-testid="hero-eyebrow">
                            The Guardian Program
                        </p>
                        <h2 className="font-display font-semibold tracking-tight text-4xl sm:text-5xl lg:text-6xl leading-[1.05]" data-testid="hero-heading">
                            The Parramatta River is drowning in plastic.
                            <span className="text-sky-200"> We built the service that pays to fix it.</span>
                        </h2>
                        <p className="mt-6 text-lg md:text-xl text-slate-300 max-w-2xl leading-relaxed" data-testid="hero-subheading">
                            RiverGuardian deploys solar-powered, semi-autonomous Guardians under annual
                            council and utility contracts, removing plastics, publishing open water-quality
                            data, and returning recycled material to Country. No donations. No grants. A
                            service that funds itself.
                        </p>
                        <div className="mt-8 flex flex-wrap gap-4">
                            <button
                                data-testid="hero-cta-tender"
                                onClick={() => scrollTo("contact")}
                                className="inline-flex items-center gap-2 bg-sky-400 hover:bg-sky-300 text-[#020D18] font-semibold text-base px-6 py-3 rounded-full transition-[background-color,transform] duration-300 hover:scale-[1.04] active:scale-[0.97]"
                            >
                                <FileText className="w-4 h-4" />
                                Request Tender Brief
                            </button>
                            <button
                                data-testid="hero-cta-data"
                                onClick={() => scrollTo("crisis")}
                                className="inline-flex items-center gap-2 border border-slate-500 hover:border-sky-300 hover:text-sky-200 text-slate-200 font-semibold text-base px-6 py-3 rounded-full transition-[border-color,color,transform] duration-300 hover:scale-[1.04] active:scale-[0.97]"
                            >
                                <ArrowDown className="w-4 h-4" />
                                Explore the Data
                            </button>
                        </div>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, y: 24 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.8, delay: 0.25, ease: "easeOut" }}
                        className="mt-14 grid grid-cols-2 lg:grid-cols-4 gap-px bg-[#12283C] border border-[#12283C] rounded-xl overflow-hidden"
                        data-testid="hero-stats-strip"
                    >
                        {HERO_STATS.map((stat, i) => (
                            <div key={i} className="bg-[#020D18]/90 backdrop-blur p-5" data-testid={`hero-stat-${i}`}>
                                <p className="font-display font-semibold text-3xl text-sky-200">{stat.value}</p>
                                <p className="mt-1 text-sm text-slate-400 leading-snug">{stat.label}</p>
                            </div>
                        ))}
                    </motion.div>
                </div>
            </div>
            <WaveDivider fill="#020D18" testid="hero-wave" />
        </section>
    );
}
